import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import PageLoader from '../components/common/Loader/PageLoader';
import ScrollToTop from '../components/ScrollToTop';
import LEGAL_DOCS from '../pages/Legal/legalIndex';

// Main pages
const Home = lazy(() => import('../pages/Home/Home'));
const About = lazy(() => import('../pages/About/About'));
const Pricing = lazy(() => import('../pages/Pricing/Pricing'));
const Demo = lazy(() => import('../pages/Demo/Demo'));
const Contact = lazy(() => import('../pages/Contact/Contact'));
const Download = lazy(() => import('../pages/Download/Download'));

// Features
const FeaturesHub = lazy(() => import('../pages/Features/FeaturesHub'));
const AISchoolManagement = lazy(() => import('../pages/Features/AISchoolManagement'));
const AdmissionManagement = lazy(() => import('../pages/Features/AdmissionManagement'));
const AcademicManagement = lazy(() => import('../pages/Features/AcademicManagement'));
const AttendanceManagement = lazy(() => import('../pages/Features/AttendanceManagement'));
const FeesManagement = lazy(() => import('../pages/Features/FeesManagement'));
const StaffManagement = lazy(() => import('../pages/Features/StaffManagement'));
const AssessmentManagement = lazy(() => import('../pages/Features/AssessmentManagement'));
const TimetableManagement = lazy(() => import('../pages/Features/TimetableManagement'));
const StudentInformationSystem = lazy(() => import('../pages/Features/StudentInformationSystem'));
const HostelManagement = lazy(() => import('../pages/Features/HostelManagement'));
const TransportationManagement = lazy(() => import('../pages/Features/TransportationManagement'));
const LibraryManagement = lazy(() => import('../pages/Features/LibraryManagement'));
const NotificationManagement = lazy(() => import('../pages/Features/NotificationSystem'));
const CourseManagement = lazy(() => import('../pages/Features/CourseManagement'));
const LearningManagementSystem = lazy(() => import('../pages/Features/LearningManagementSystem'));
const SyllabusTracking = lazy(() => import('../pages/Features/SyllabusTracking'));
const Communication = lazy(() => import('../pages/Features/Communication'));
const InventoryAssets = lazy(() => import('../pages/Features/InventoryAssets'));
const AccountingFinance = lazy(() => import('../pages/Features/AccountingFinance'));
const ReportsAnalytics = lazy(() => import('../pages/Features/ReportsAnalytics'));
const MobileApp = lazy(() => import('../pages/Features/MobileApp'));
const SecurityPrivacy = lazy(() => import('../pages/Features/SecurityPrivacy'));

// Case Studies

// Projects
const ProjectsList = lazy(() => import('../pages/Projects/ProjectsList'));
const ProjectDetail = lazy(() => import('../pages/Projects/ProjectDetail'));

// Solutions
const SolutionsHub = lazy(() => import('../pages/Solutions/SolutionsHub'));
const ForSchools = lazy(() => import('../pages/Solutions/ForSchools'));
const ForStudents = lazy(() => import('../pages/Solutions/ForStudents'));
const ForTeachers = lazy(() => import('../pages/Solutions/ForTeachers'));
const ForParents = lazy(() => import('../pages/Solutions/ForParents'));
const ForColleges = lazy(() => import('../pages/Solutions/ForColleges'));
const ForUniversities = lazy(() => import('../pages/Solutions/ForUniversities'));
const ForTuition = lazy(() => import('../pages/Solutions/ForTuition'));
const ForLMS = lazy(() => import('../pages/Solutions/ForLMS'));

// Resources
const Help = lazy(() => import('../pages/Resources/Help'));

// Blog
const BlogList = lazy(() => import('../pages/Blog/BlogList'));
const BlogDetail = lazy(() => import('../pages/Blog/BlogDetail'));

// Auth
const Login = lazy(() => import('../pages/Auth/Login'));
const Signup = lazy(() => import('../pages/Auth/Signup'));
const ForgotPassword = lazy(() => import('../pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/Auth/ResetPassword'));

// Legal
const LegalCenter = lazy(() => import('../pages/Legal/LegalCenter'));
const LegalDocPage = lazy(() => import('../pages/Legal/LegalDocPage'));

// Specialty
const Updates = lazy(() => import('../pages/Specialty/Updates'));
const Migration = lazy(() => import('../pages/Specialty/Migration'));
const ApiDocs = lazy(() => import('../pages/Specialty/ApiDocs'));

// Other pages
const Integrations = lazy(() => import('../pages/Integrations/Integrations'));
const Gallery = lazy(() => import('../pages/Gallery/Gallery'));
const Support = lazy(() => import('../pages/Support/Support'));
const Partners = lazy(() => import('../pages/Partners/Partners'));

// Admissions
const AdmissionsHome = lazy(() => import('../pages/Admissions/AdmissionsHome'));
const ApplyOnline = lazy(() => import('../pages/Admissions/ApplyOnline'));
const AdmissionProcess = lazy(() => import('../pages/Admissions/AdmissionProcess'));
const ApplicationStatus = lazy(() => import('../pages/Admissions/ApplicationStatus'));
const DownloadBrochure = lazy(() => import('../pages/Admissions/DownloadBrochure'));

// Utility
const Search = lazy(() => import('../pages/Utility/Search'));
const Sitemap = lazy(() => import('../pages/Utility/Sitemap'));
const NotFound = lazy(() => import('../pages/Utility/NotFound'));

const AppRoutes = () => {
  return (
    <Layout>
<Suspense fallback={
  <div style={{
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    zIndex: 9998
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(99,102,241,0.2)',
      borderTopColor: '#6366f1',
      borderRadius: '50%',
      animation: 'appSpin 0.8s linear infinite'
    }} />
    <span style={{
      fontSize: '14px',
      color: '#6b7280',
      fontFamily: 'system-ui, sans-serif'
    }}>
      Loading...
    </span>
  </div>
}>        <ScrollToTop />
        <Routes>

          {/* ✅ Main Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/download" element={<Download />} />

          {/* ✅ Duplicate URL Redirects — fixes Soft 404 + duplicate content */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/hello-world" element={<Navigate to="/" replace />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />

          {/* ✅ Keyword landing pages */}
          <Route path="/ai-school-management-system" element={<AISchoolManagement />} />

          {/* ✅ Features */}
          <Route path="/features" element={<FeaturesHub />} />
          <Route path="/features/admission-management" element={<AdmissionManagement />} />
          <Route path="/features/academic-management" element={<AcademicManagement />} />
          <Route path="/features/attendance-management" element={<AttendanceManagement />} />
          <Route path="/features/fees-management" element={<FeesManagement />} />
          <Route path="/features/staff-management" element={<StaffManagement />} />
          <Route path="/features/assessment-examination" element={<AssessmentManagement />} />
          <Route path="/features/timetable-management" element={<TimetableManagement />} />
          <Route path="/features/student-information-system" element={<StudentInformationSystem />} />
          <Route path="/features/hostel-management" element={<HostelManagement />} />
          <Route path="/features/transportation-management" element={<TransportationManagement />} />
          <Route path="/features/library-management" element={<LibraryManagement />} />
          <Route path="/features/notification-system" element={<NotificationManagement />} />
          <Route path="/features/course-management" element={<CourseManagement />} />
          <Route path="/features/learning-management-system" element={<LearningManagementSystem />} />
          <Route path="/features/syllabus-tracking" element={<SyllabusTracking />} />
          <Route path="/features/communication" element={<Communication />} />
          <Route path="/features/inventory-assets" element={<InventoryAssets />} />
          <Route path="/features/accounting-finance" element={<AccountingFinance />} />
          <Route path="/features/reports-analytics" element={<ReportsAnalytics />} />
          <Route path="/features/mobile-app" element={<MobileApp />} />
          <Route path="/features/security-privacy" element={<SecurityPrivacy />} />

          {/* ✅ Case Studies */}
          <Route path="/case-studies" element={<Navigate to="/" replace />} />
          <Route path="/case-studies/:slug" element={<Navigate to="/" replace />} />

          {/* ✅ Projects */}
          <Route path="/projects" element={<ProjectsList />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />

          {/* ✅ Solutions — organized by institution type */}
          <Route path="/solutions" element={<SolutionsHub />} />
          <Route path="/solutions/schools" element={<ForSchools />} />
          <Route path="/solutions/college" element={<ForColleges />} />
          <Route path="/solutions/tuition" element={<ForTuition />} />
          <Route path="/solutions/universities" element={<ForUniversities />} />
          <Route path="/solutions/lms" element={<ForLMS />} />

          {/* Legacy stakeholder pages — no longer promoted from the Solutions
              hub or sitemap, but left live in case of existing inbound links */}
          <Route path="/solutions/for-parents" element={<ForParents />} />
          <Route path="/solutions/for-students" element={<ForStudents />} />
          <Route path="/solutions/for-teachers" element={<ForTeachers />} />

          {/* Old typo/renamed URL redirects */}
          <Route path="/solutions/for-perents" element={<Navigate to="/solutions/for-parents" replace />} />
          <Route path="/solutions/colleges" element={<Navigate to="/solutions/college" replace />} />
          <Route path="/solutions/for-colleges" element={<Navigate to="/solutions/college" replace />} />

          {/* ✅ Resources */}
          <Route path="/resources" element={<Navigate to="/blog" replace />} />
          <Route path="/help" element={<Help />} />
          <Route path="/docs" element={<Navigate to="/help" replace />} />
          <Route path="/tutorials" element={<Navigate to="/help" replace />} />

          {/* ✅ Blog */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />

          {/* ✅ Auth — noindex these in SEO component */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* ✅ Legal */}
          <Route path="/legal" element={<LegalCenter />} />
          {LEGAL_DOCS.map((d) => (
            <Route key={d.slug} path={d.path} element={<LegalDocPage key={d.slug} slug={d.slug} />} />
          ))}
          <Route path="/gdpr" element={<Navigate to="/privacy" replace />} />
          <Route path="/subscription-agreement" element={<Navigate to="/legal" replace />} />
          <Route path="/data-processing-agreement" element={<Navigate to="/legal" replace />} />
          <Route path="/parental-consent-template" element={<Navigate to="/legal" replace />} />

          {/* ✅ Specialty */}
          <Route path="/careers" element={<Navigate to="/about" replace />} />
          <Route path="/updates" element={<Updates />} />
          <Route path="/migration" element={<Migration />} />
          <Route path="/api-docs" element={<ApiDocs />} />

          {/* ✅ Other Pages */}
          <Route path="/customers" element={<Navigate to="/" replace />} />
          <Route path="/reviews" element={<Navigate to="/" replace />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/support" element={<Support />} />
          <Route path="/partners" element={<Partners />} />

          {/* ✅ Admissions */}
          <Route path="/admissions" element={<AdmissionsHome />} />
          <Route path="/admissions/apply" element={<ApplyOnline />} />
          <Route path="/admissions/process" element={<AdmissionProcess />} />
          <Route path="/admissions/status" element={<ApplicationStatus />} />
          <Route path="/admissions/brochure" element={<DownloadBrochure />} />

          {/* ✅ Utility */}
          <Route path="/search" element={<Search />} />
          <Route path="/sitemap" element={<Sitemap />} />

          {/* ✅ Redirect shortcuts */}
          <Route path="/trial" element={<Navigate to="/demo" replace />} />
          <Route path="/security" element={<Navigate to="/features/security-privacy" replace />} />
          <Route path="/status" element={<Navigate to="/contact" replace />} />

          {/* ✅ 404 */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
    </Layout>
  );
};

export default AppRoutes;
